'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { Mail, Loader2, ArrowLeft, MailCheck } from 'lucide-react'

/* Wachtwoord vergeten: het adres invullen, meer niet.
 *
 * De pagina zegt altijd hetzelfde, of het adres nu bekend is of niet. Dat is
 * geen vaagheid maar opzet: anders kan iemand hier uitproberen wie er klant
 * bij ons is. De API doet hetzelfde; zie src/app/api/auth/password/forgot. */

const schema = z.object({
  email: z.string().email('Voer een geldig e-mailadres in'),
})

type FormData = z.infer<typeof schema>

export default function WachtwoordVergetenPage() {
  const [sent, setSent]       = useState(false)
  const [loading, setLoading] = useState(false)
  const [notice, setNotice]   = useState('')

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: FormData) => {
    setLoading(true)
    try {
      const res  = await fetch('/api/auth/password/forgot', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: data.email }),
      })
      const json = await res.json().catch(() => ({}))
      setNotice(json.message ?? 'Als dit e-mailadres bij ons bekend is, ontvangt u zo een e-mail.')
      setSent(true)
    } catch {
      // Ook een storing mag niet verklappen of het adres bestaat.
      setNotice('Als dit e-mailadres bij ons bekend is, ontvangt u zo een e-mail.')
      setSent(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-16"
      style={{
        backgroundColor: '#FDF6EC',
        backgroundImage: `radial-gradient(circle at 20% 50%, rgba(139,69,19,0.06) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(218,165,32,0.08) 0%, transparent 40%),
          radial-gradient(circle at 60% 80%, rgba(210,105,30,0.05) 0%, transparent 40%)`,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div
          className="rounded-3xl p-8 sm:p-10"
          style={{ background: '#fff', boxShadow: '0 20px 60px rgba(139,69,19,0.15), 0 4px 20px rgba(139,69,19,0.08)' }}
        >
          <div className="text-center mb-8">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
              style={{ background: 'linear-gradient(135deg, #8B4513, #D2691E)' }}
            >
              <span className="text-2xl">🥖</span>
            </div>
            <h1
              className="text-2xl font-bold tracking-tight"
              style={{ fontFamily: 'var(--font-dancing)', color: '#8B4513', fontSize: '1.6rem' }}
            >
              Bakkerij Voortman
            </h1>
          </div>

          {sent ? (
            <div className="text-center">
              <div
                className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
                style={{ background: '#F0FDF4', color: '#3E7A34' }}
              >
                <MailCheck size={24} />
              </div>
              <h2
                className="text-2xl font-bold mb-3"
                style={{ fontFamily: 'var(--font-playfair)', color: '#2C1810' }}
              >
                Kijk in je mailbox
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: '#8B4513' }}>
                {notice}
              </p>
              <p className="mt-3 text-xs leading-relaxed" style={{ color: '#A07850' }}>
                Niets ontvangen? Kijk ook even in de ongewenste post. De link is een uur geldig.
              </p>
              <Link
                href="/inloggen"
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold"
                style={{ color: '#8B4513' }}
              >
                <ArrowLeft size={15} /> Terug naar inloggen
              </Link>
            </div>
          ) : (
            <>
              <h2
                className="text-3xl font-bold text-center mb-3"
                style={{ fontFamily: 'var(--font-playfair)', color: '#2C1810' }}
              >
                Wachtwoord vergeten
              </h2>
              <p className="text-center text-sm mb-8 leading-relaxed" style={{ color: '#8B4513' }}>
                Vul je e-mailadres in. We sturen je een link waarmee je zelf een nieuw wachtwoord kiest.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold mb-1.5" style={{ color: '#2C1810' }}>
                    E-mailadres
                  </label>
                  <div className="relative">
                    <Mail
                      size={16}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
                      style={{ color: '#8B4513' }}
                    />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="naam@voorbeeld.nl"
                      {...register('email')}
                      className="w-full rounded-xl py-3 pl-10 pr-4 text-sm outline-none transition-colors"
                      style={{
                        background: '#FDF6EC',
                        border: errors.email ? '1.5px solid #DC2626' : '1.5px solid #E8D5C4',
                        color: '#2C1810',
                      }}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-xs" style={{ color: '#DC2626' }}>{errors.email.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-xl py-3 text-sm font-bold text-white transition-transform active:scale-[0.99] disabled:opacity-70"
                  style={{ background: 'linear-gradient(135deg, #8B4513, #D2691E)' }}
                >
                  {loading ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin" /> Bezig...
                    </span>
                  ) : (
                    'Stuur mij een link'
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  href="/inloggen"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold"
                  style={{ color: '#8B4513' }}
                >
                  <ArrowLeft size={15} /> Terug naar inloggen
                </Link>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  )
}
